/**
 * Firebase init for the auth page.
 *
 * The Horus Firebase project's web config is INTENTIONALLY publishable
 * — these values ship to every browser that loads the auth page. Treat
 * as documentation, not as a secret. The actual security boundary is
 * (a) Firebase's authorized-domains allow-list (which permits this
 * page to use OAuth flows because auth.horuswallet.com is on it) and
 * (b) the postMessage origin validation in postmessage.ts.
 *
 * Real values come from the Horus Firebase Console → Project Settings
 * → General → Your apps → Web. Read at runtime via `import.meta.env`
 * so different stage builds can point at different Firebase projects
 * without code changes.
 */

import { initializeApp, FirebaseApp } from 'firebase/app';
import { getAuth, Auth } from 'firebase/auth';

/**
 * Resolve the Firebase `authDomain`.
 *
 * Firebase routes every OAuth redirect/popup through
 * `<authDomain>/__/auth/handler`. Pointing that at
 * `<project>.firebaseapp.com` makes sign-in visibly traverse a second
 * domain — the user clicks a partner's button and watches the window
 * go auth.horuswallet.com → social-wallet-c5b58.firebaseapp.com →
 * back. It also puts the relay's storage in a third-party context,
 * which is precisely what Brave's Shields block (the reason
 * `flows/google.ts` needs a nested-popup fallback at all).
 *
 * The CloudFront stack proxies `/__/*` to Firebase Hosting, so this
 * page's OWN origin serves the relay. Defaulting `authDomain` to the
 * current hostname therefore makes the whole exchange first-party:
 * one domain, no visible hop, no third-party storage.
 *
 * Order:
 *   1. `VITE_FIREBASE_AUTH_DOMAIN` — explicit wins, always.
 *   2. `window.location.hostname` on a deployed origin — first-party.
 *   3. `*.firebaseapp.com` on localhost, where nothing proxies `/__/*`.
 *
 * Case 3 matters: `npm run dev` serves only this Vite app, so a
 * self-hosted relay would 404 and every OAuth flow would die locally.
 * Local dev keeps the third-party relay and the extra hop.
 */
const LOCAL_HOSTS = new Set(['localhost', '127.0.0.1', '[::1]', '0.0.0.0']);
const FALLBACK_AUTH_DOMAIN = 'social-wallet-c5b58.firebaseapp.com';

function resolveAuthDomain(): string {
  const explicit = import.meta.env.VITE_FIREBASE_AUTH_DOMAIN;
  if (explicit) return explicit;
  if (typeof window === 'undefined') return FALLBACK_AUTH_DOMAIN;
  const host = window.location.hostname;
  if (!host || LOCAL_HOSTS.has(host)) return FALLBACK_AUTH_DOMAIN;
  return host;
}

// Defaults are the production Horus Firebase web config (publishable).
// Override per-stage by setting VITE_FIREBASE_* in .env.local.
const firebaseConfig = {
  apiKey:
    import.meta.env.VITE_FIREBASE_API_KEY ??
    'AIzaSyDsWoRu2NP0jXazWyaYJAJTM6gMyOE67iY',
  authDomain: resolveAuthDomain(),
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID ?? 'social-wallet-c5b58',
  storageBucket:
    import.meta.env.VITE_FIREBASE_STORAGE_BUCKET ??
    'social-wallet-c5b58.firebasestorage.app',
  messagingSenderId:
    import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID ?? '503347440505',
  appId:
    import.meta.env.VITE_FIREBASE_APP_ID ??
    '1:503347440505:web:357ffc3210d87c528e9da8',
};

let app: FirebaseApp | null = null;
let auth: Auth | null = null;

export function getFirebaseAuth(): Auth {
  if (auth) return auth;
  if (!app) app = initializeApp(firebaseConfig);
  auth = getAuth(app);
  return auth;
}
